import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PageLayout from "@/components/PageLayout";
import { formatEUR, ttc, DEREF_PRICE_HT } from "@/config/pricing";
import { newDossierReference } from "@/lib/reference";
import { ReferenceNote } from "@/components/ReferenceNote";
import { DerefFaqSection } from "@/components/DerefFaqSection";
import { ETAPES, EXEMPLES, CRITERES, FAQ_DEREF } from "@/data/dereferencement";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isNetlifyHost = () => {
  const h = window.location.hostname;
  return h.endsWith(".netlify.app") || h === "horsindex.com" || h.endsWith(".horsindex.com");
};

function Eyebrow({ children }) {
  return <p className="label-eyebrow mb-6">{children}</p>;
}
function H2({ children }) {
  return <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl tracking-tight leading-[1.08] text-balance">{children}</h2>;
}

export default function Dereferencement() {
  const [form, setForm] = useState({ prenom: "", nom: "", email: "", requete: "", contexte: "", activite_pro: "", anciennete: "", refus_date: "", refus_resultat: "" });
  const [urls, setUrls] = useState(() => [{ id: crypto.randomUUID(), value: "" }]);
  const [googleContacte, setGoogleContacte] = useState(false);
  const [fichier, setFichier] = useState(null);
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [local, setLocal] = useState(null);
  const [answer, setAnswer] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [reference, setReference] = useState("");
  const netlify = isNetlifyHost();

  const newLocal = useCallback(() => setLocal({ a: Math.floor(Math.random() * 9) + 1, b: Math.floor(Math.random() * 9) + 1 }), []);
  const loadChallenge = useCallback(async () => {
    try { const r = await axios.get(`${API}/contact-challenge`); setChallenge(r.data); setAnswer(""); }
    catch { newLocal(); }
  }, [newLocal]);
  useEffect(() => {
    if (netlify) newLocal(); else loadChallenge();
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "seo-jsonld-faq";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQ_DEREF.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
    document.head.appendChild(ld);
    return () => ld.remove();
  }, [netlify, newLocal, loadChallenge]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setUrl = (id) => (e) => setUrls((prev) => prev.map((u) => (u.id === id ? { ...u, value: e.target.value } : u)));

  const submit = async (e) => {
    e.preventDefault();
    const cleanUrls = urls.map((u) => u.value.trim()).filter(Boolean);
    if (!form.prenom.trim() || !form.nom.trim() || !form.email.trim() || !cleanUrls.length || !form.contexte.trim()) {
      toast.error("Merci de renseigner votre identité, au moins une URL et le contexte."); return;
    }
    if (!consent) { toast.error("Merci de cocher la case de consentement."); return; }
    if (!answer.trim()) { toast.error("Merci de répondre à la question anti-spam."); return; }
    if (fichier && fichier.size > 8 * 1024 * 1024) {
      toast.error("Le fichier joint dépasse 8 Mo. Merci de joindre un document plus léger."); return;
    }
    if (local && Number(answer) !== local.a + local.b) {
      toast.error("Le résultat de la vérification anti-spam est incorrect."); newLocal(); setAnswer(""); return;
    }
    setSending(true);
    const ref = newDossierReference();
    try {
      if (netlify) {
        const fd = new FormData();
        fd.append("form-name", "contact");
        fd.append("origine", "desindexation");
        fd.append("reference", ref);
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        fd.append("urls", cleanUrls.join("\n"));
        fd.append("google_contacte", googleContacte ? "oui" : "non");
        fd.append("consentement", "oui");
        if (fichier) fd.append("fichier", fichier);
        const r = await fetch("/", { method: "POST", body: fd });
        if (!r.ok) throw new Error("netlify-form-error");
      } else {
        await axios.post(`${API}/dereferencement`, {
          ...form, urls: cleanUrls, google_contacte: googleContacte, consentement: true,
          reference: ref,
          website: honeypot, captcha_token: challenge?.token || "", captcha_answer: answer,
        });
      }
      setReference(ref);
      setSent(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "L'envoi a échoué. Merci de réessayer dans un instant.");
      if (!netlify) loadChallenge();
    } finally { setSending(false); }
  };

  const question = challenge ? challenge.question : local ? `${local.a} + ${local.b}` : "…";
  const field = "input-line w-full";

  return (
    <PageLayout
      eyebrow="Déréférencement Google"
      title="Un résultat Google apparaît lorsque vous recherchez votre nom ?"
      intro="HorsIndex vous accompagne pour obtenir le déréférencement des résultats Google associés à votre identité. Chaque URL est analysée individuellement afin de construire l'argumentation la plus adaptée et de maximiser les chances d'aboutir."
      hideFooterCta
      pattern="indexation"
    >
      {/* CTA hero */}
      <section className="bg-[#FAF9F7] border-b border-[#E2DEDA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-16">
          <div className="flex flex-wrap items-center gap-4">
            <a href="#deref-form" data-testid="deref-cta-analyser" className="btn-primary">
              Faire analyser un résultat
            </a>
            <a href="#deref-comprendre" data-testid="deref-cta-comprendre" className="btn-ghost">
              Comprendre le déréférencement
            </a>
          </div>
          <p data-testid="deref-prix-hero" className="mt-8 font-mono text-[14px] lg:text-[15px] font-medium tracking-tight text-[#181A1D]">
            <span className="block">
              À partir de {formatEUR(DEREF_PRICE_HT)}&nbsp;HT par URL
              <span className="ml-3 font-sans text-[13px] font-normal text-[#666A70] tracking-normal align-middle">
                Tarif confirmé avant toute intervention.
              </span>
            </span>
            <span className="block mt-1 text-[12px] font-normal text-[#8B8F95]">TVA en sus au taux applicable</span>
          </p>
          <p data-testid="deref-mention-moyens" className="mt-3 text-[12px] text-[#5A5E64] font-normal leading-relaxed max-w-2xl">
            La décision de déréférencement appartient à Google. HorsIndex est tenu à une
            obligation de moyens et ne peut garantir la décision du moteur de recherche.
          </p>
        </div>
      </section>

      {/* Étapes */}
      <section className="bg-[#F2F0ED] border-b border-[#E2DEDA] py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Eyebrow>Prise en charge</Eyebrow>
          <H2>L'objectif : faire déréférencer le résultat associé à votre nom.</H2>
          <ol className="mt-8 border-t border-[#E2DEDA]">
            {ETAPES.map((s, i) => (
              <li key={s.t} data-testid={`deref-etape-${i}`} className="border-b border-[#E2DEDA] py-7 lg:py-8 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 items-baseline">
                <span className="md:col-span-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#8B8F95]">Étape {String(i + 1).padStart(2, "0")} / 05</span>
                <h3 className="md:col-span-4 font-serif text-xl text-[#181A1D] font-normal leading-snug tracking-tight text-balance">{s.t}</h3>
                <p className="md:col-span-6 text-[14px] text-[#5A5E64] font-normal leading-relaxed text-pretty">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Déréférencer ≠ supprimer */}
      <section id="deref-comprendre" className="bg-[#FAF9F7] border-b border-[#E2DEDA] py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>Résultat concret</Eyebrow>
            <H2>Quand une demande <em className="italic font-normal">aboutit</em>.</H2>
            <p className="mt-8 text-[15px] text-[#52565C] font-normal leading-relaxed">
              Le résultat concerné cesse d'apparaître pour les recherches nominatives visées,
              par exemple « Prénom Nom ». La page source peut continuer d'exister, mais elle
              n'est plus directement associée à votre identité par cette recherche. C'est ce
              que l'on appelle le déréférencement, ou dans le langage courant le « droit à
              l'oubli Google » : agir sur la visibilité du résultat, sans nécessairement
              supprimer la page elle-même.
            </p>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center gap-3" data-testid="deref-schema">
            <div className="bg-white rounded-md border border-[#E2DEDA] p-7">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8B8F95] mb-2">Site d'origine</p>
              <p className="font-serif text-xl text-[#181A1D] font-normal">La page peut continuer à exister.</p>
            </div>
            <div className="text-center text-[#8B8F95] text-xl" aria-hidden="true">↓</div>
            <div className="bg-[#181A1D] rounded-md p-7">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8B8F95] mb-2">Google</p>
              <p className="font-serif text-xl text-white font-normal">Le résultat cesse d'apparaître pour les recherches nominatives visées.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Exemples */}
      <section className="bg-[#F2F0ED] border-b border-[#E2DEDA] py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Eyebrow>Situations fréquentes</Eyebrow>
          <H2>Quels résultats peuvent être concernés ?</H2>
          <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
            {EXEMPLES.map((x, i) => (
              <li key={x} data-testid={`deref-exemple-${i}`} className="border-l border-[#D8D3CD] pl-5 py-1 font-serif text-lg text-[#181A1D] font-normal leading-snug">{x}</li>
            ))}
          </ul>
          <p className="mt-8 text-[13px] text-[#666A70] font-normal max-w-3xl">
            Chaque situation est différente : l'analyse initiale permet de déterminer si une
            demande est défendable et sur quels arguments la construire.
          </p>
        </div>
      </section>

      {/* Analyse */}
      <section className="bg-[#FAF9F7] border-b border-[#E2DEDA] py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Eyebrow>Analyse individualisée</Eyebrow>
          <H2>Comment HorsIndex analyse votre demande.</H2>
          <p className="mt-8 max-w-3xl text-[15px] text-[#52565C] font-normal leading-relaxed">
            Les demandes de déréférencement sont appréciées au cas par cas : c'est précisément
            là que se joue l'issue d'un dossier. HorsIndex examine la nature de l'information,
            son ancienneté, sa pertinence actuelle, la situation de la personne concernée, son
            éventuel rôle public et l'intérêt que pourrait avoir le public à retrouver cette
            information à partir de son identité, afin de retenir les arguments les plus
            solides pour votre demande.
          </p>
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
            {CRITERES.map((c) => (
              <li key={c.t} className="border-l border-[#D8D3CD] pl-6 py-1">
                <h3 className="font-serif text-xl text-[#181A1D] font-normal mb-3">{c.t}</h3>
                <p className="text-sm text-[#666A70] font-normal leading-relaxed">{c.d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Tarification */}
      <section className="bg-[#181A1D] py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="label-eyebrow text-[#8B8F95] mb-6">Tarification</p>
            <h2 className="font-serif font-normal text-white text-3xl lg:text-4xl tracking-tight leading-[1.08]">Une analyse individualisée, pour chaque URL.</h2>
            <p className="mt-6 text-[14px] text-[#B4B8BD] font-normal leading-relaxed">
              Chaque résultat fait l'objet d'une analyse individualisée. Le tarif comprend
              l'évaluation du dossier, la préparation de l'argumentation, l'accompagnement de la
              demande et le suivi de la réponse de Google.
            </p>
            <p data-testid="deref-prix-bloc" className="mt-6 font-mono text-[15px] lg:text-base font-medium tracking-tight text-[#FAF9F7]">
              <span className="block">À partir de {formatEUR(DEREF_PRICE_HT)}&nbsp;HT par URL</span>
              <span className="block mt-1 text-[12px] font-normal text-[#B4B8BD]">TVA en sus au taux applicable</span>
            </p>
            <p className="mt-4 text-[13px] text-[#8B8F95] font-normal leading-relaxed">
              Le tarif peut varier selon la complexité du dossier, la nature de la source, le
              nombre d'URL et l'existence éventuelle d'un refus antérieur. Le prix définitif est
              confirmé avant intervention, par un devis établi dans le cadre défini par les{" "}
              <Link
                to="/conditions-generales"
                data-testid="deref-link-cgv"
                className="text-[#B4B8BD] underline decoration-[#4A4E54] underline-offset-4 hover:text-[#FAF9F7] hover:decoration-[#FAF9F7] transition-colors"
              >
                conditions générales
              </Link>.
            </p>
          </div>
          <ul className="lg:col-span-7 space-y-3 self-center">
            {["Analyse du résultat", "Qualification de la situation", "Préparation d'une argumentation individualisée", "Préparation ou accompagnement de la demande auprès de Google", "Suivi du résultat de la demande", "Analyse de la réponse de Google en cas de refus"].map((x) => (
              <li key={x} className="flex items-start gap-3 text-[15px] text-[#B4B8BD] font-normal leading-relaxed">
                <span aria-hidden="true" className="mt-[11px] w-1 h-1 rounded-full bg-[#FAF9F7]0 flex-shrink-0" />{x}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Refus */}
      <section className="bg-[#F2F0ED] border-b border-[#E2DEDA] py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Eyebrow>Refus</Eyebrow>
          <H2>Google a déjà refusé votre demande ?</H2>
          <p className="mt-8 max-w-3xl text-[15px] text-[#52565C] font-normal leading-relaxed">
            Un premier refus ne signifie pas nécessairement qu'aucune démarche complémentaire
            n'est possible. HorsIndex peut analyser le motif communiqué par Google, vérifier si
            les éléments essentiels du dossier ont été correctement pris en compte et déterminer
            si une demande de réexamen est défendable.
          </p>
          <a href="#deref-form" data-testid="deref-cta-refus" className="mt-8 btn-ghost">
            Faire analyser un refus Google
          </a>
        </div>
      </section>

      {/* Formulaire */}
      <section id="deref-form" className="bg-[#FAF9F7] border-b border-[#E2DEDA] py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Eyebrow>Demande d'analyse</Eyebrow>
          <H2>Demander une analyse.</H2>
          {sent ? (
            <div data-testid="deref-success" className="mt-10 bg-white rounded-md border border-[#E2DEDA] p-8 lg:p-10">
              <p className="label-eyebrow mb-4">Demande transmise</p>
              <p className="font-serif text-2xl text-[#181A1D] font-normal leading-snug mb-4">Merci pour votre demande.</p>
              <p className="text-[15px] text-[#5A5E64] font-normal leading-relaxed">
                HorsIndex examine votre situation et revient vers vous à l'adresse indiquée,
                généralement sous quelques jours ouvrés. Le tarif définitif vous sera confirmé
                avant toute intervention.
              </p>
              <ReferenceNote reference={reference} testId="deref-reference" className="mt-6" />
            </div>
          ) : (
            <form onSubmit={submit} data-testid="deref-formulaire" className="mt-10 bg-white rounded-md border border-[#E2DEDA] shadow-sm p-8 lg:p-10 space-y-8">
              <div>
                <p className="label-eyebrow mb-5">Votre identité</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <input type="text" placeholder="Prénom *" value={form.prenom} onChange={set("prenom")} data-testid="deref-prenom" className={field} autoComplete="given-name" />
                  <input type="text" placeholder="Nom *" value={form.nom} onChange={set("nom")} data-testid="deref-nom" className={field} autoComplete="family-name" />
                </div>
                <input type="email" placeholder="Adresse e-mail *" value={form.email} onChange={set("email")} data-testid="deref-email" className={`${field} mt-6`} autoComplete="email" />
              </div>
              <div>
                <p className="label-eyebrow mb-5">Résultat concerné</p>
                {urls.map((u, i) => (
                  <input key={u.id} type="url" placeholder={`URL du résultat ${i + 1} *`} value={u.value} onChange={setUrl(u.id)} data-testid={`deref-url-${i}`} className={`${field} ${i > 0 ? "mt-4" : ""}`} />
                ))}
                <button type="button" onClick={() => setUrls([...urls, { id: crypto.randomUUID(), value: "" }])} data-testid="deref-add-url" className="mt-4 py-2.5 text-[11px] tracking-[0.18em] uppercase font-medium text-[#181A1D] underline decoration-[#D8D3CD] underline-offset-4 hover:decoration-[#181A1D] transition-colors">
                  + Ajouter une autre URL
                </button>
                <input type="text" placeholder="Requête Google permettant de retrouver le résultat (généralement « prénom nom »)" value={form.requete} onChange={set("requete")} data-testid="deref-requete" className={`${field} mt-6`} />
              </div>
              <div>
                <p className="label-eyebrow mb-5">Contexte</p>
                <textarea rows={5} placeholder="Pourquoi souhaitez-vous que ce résultat n'apparaisse plus lorsque votre nom est recherché ? *" value={form.contexte} onChange={set("contexte")} data-testid="deref-contexte" className={`${field} resize-y`} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <select value={form.activite_pro} onChange={set("activite_pro")} data-testid="deref-activite" className={field}>
                    <option value="">Activité professionnelle concernée ?</option>
                    <option value="actuelle">Oui, activité actuelle</option>
                    <option value="passee">Oui, activité passée</option>
                    <option value="non">Non</option>
                  </select>
                  <input type="text" placeholder="Date approximative de l'information" value={form.anciennete} onChange={set("anciennete")} data-testid="deref-anciennete" className={field} />
                </div>
                <label className="mt-6 flex items-center gap-3 cursor-pointer text-[14px] text-[#52565C] font-normal">
                  <input type="checkbox" checked={googleContacte} onChange={(e) => setGoogleContacte(e.target.checked)} data-testid="deref-google-contacte" className="h-4 w-4 accent-[#E94B3C]" />
                  Google a-t-il déjà refusé le déréférencement de cette URL ?
                </label>
                {googleContacte && (
                  <div className="mt-4 pl-4 border-l-2 border-[#E2DEDA] space-y-4" data-testid="deref-refus-bloc">
                    <input type="text" placeholder="Date approximative de la demande" value={form.refus_date} onChange={set("refus_date")} data-testid="deref-refus-date" className={field} />
                    <select value={form.refus_resultat} onChange={set("refus_resultat")} data-testid="deref-refus-resultat" className={field}>
                      <option value="">Résultat obtenu</option>
                      <option value="refus">Refus</option>
                      <option value="sans-reponse">Absence de réponse</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                )}
                {netlify && (
                  <div className="mt-6">
                    <label className="label-eyebrow block mb-3">Capture d'écran ou réponse de Google (facultatif)</label>
                    <input type="file" accept="image/*,.pdf" onChange={(e) => setFichier(e.target.files?.[0] || null)} data-testid="deref-fichier" className="block text-[13px] text-[#5A5E64] font-normal" />
                  </div>
                )}
              </div>
              <div className="hidden" aria-hidden="true">
                <input type="text" name="bot-field" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
              </div>
              <div className="border-t border-[#E2DEDA] pt-6">
                <p className="label-eyebrow mb-3">Vérification anti-spam</p>
                <div className="flex flex-wrap items-center gap-4">
                  <span data-testid="deref-captcha-question" className="font-serif text-xl text-[#181A1D] select-none">{question} =</span>
                  <input type="text" inputMode="numeric" autoComplete="off" value={answer} onChange={(e) => setAnswer(e.target.value.replace(/[^0-9-]/g, ""))} data-testid="deref-captcha-input" className="input-line max-w-[6rem]" placeholder="?" aria-label="Vérification anti-spam" />
                </div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="deref-consent" className="mt-1 h-4 w-4 accent-[#E94B3C]" />
                <span className="text-[13px] text-[#5A5E64] font-normal leading-relaxed">
                  J'accepte que ces informations soient utilisées uniquement pour l'analyse de ma
                  demande.{" "}
                  <Link to="/politique-confidentialite" className="underline decoration-[#D8D3CD] underline-offset-4 text-[#181A1D] hover:decoration-[#181A1D] transition-colors">Politique de confidentialité</Link>.
                </span>
              </label>
              <p className="text-[13px] text-[#666A70] font-normal leading-relaxed" data-testid="deref-avertissement">
                Une demande d'analyse n'implique ni acceptation automatique du dossier ni garantie
                de déréférencement. Aucune pièce d'identité n'est demandée à ce stade.
              </p>
              <button type="submit" disabled={sending} data-testid="deref-submit" className="btn-primary disabled:opacity-50">
                {sending ? "Envoi…" : "Demander une analyse"}
              </button>
            </form>
          )}
        </div>
      </section>

      <DerefFaqSection />
    </PageLayout>
  );
}
